import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useMeetingHubContext } from 'components/meetings/MeetingPage/MeetingHubContext';
import MeetingService from 'services/MeetingService';
import useRequest from 'hooks/useRequest';

import styles from './VotingTimerView.module.scss';

const formatTime = (remainingSeconds: number) => {
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  return minutes > 0 ? `${minutes}:${String(seconds).padStart(2, '0')}` : `${seconds}s`;
};

const VotingTimerView: React.FunctionComponent = () => {
  const { meetingSession } = useMeetingHubContext();
  const voting = meetingSession?.currentVotingSession;

  const endsAtMs = useMemo(() => {
    const time = voting?.timerEndsAt && new Date(voting.timerEndsAt).getTime();
    return Number.isFinite(time) ? time : null;
  }, [voting?.timerEndsAt]);

  const active = !!endsAtMs && voting?.isVotingAvailable && !voting?.closedAt;

  const getServerTimeRequest = useMemo(() => (!active ? undefined : () => MeetingService.getServerTime()), [active]);
  const { data: serverTime } = useRequest<string>(getServerTimeRequest);

  const offsetMs = useMemo(() => {
    if (!serverTime) return 0;

    const serverNowMs = Date.parse(serverTime);
    if (!Number.isFinite(serverNowMs)) return 0;

    return serverNowMs - Date.now();
  }, [serverTime]);

  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);

  useEffect(() => {
    if (!active || !endsAtMs) {
      setRemainingSeconds(null);
      return;
    }

    const update = () => {
      const nowMs = Date.now() + offsetMs;
      const secondsLeft = Math.max(0, Math.ceil((endsAtMs - nowMs) / 1000));
      setRemainingSeconds(secondsLeft);
      return secondsLeft;
    };

    if (update() === 0) return;

    const id = window.setInterval(() => {
      if (update() === 0) {
        window.clearInterval(id);
      }
    }, 1000);

    return () => window.clearInterval(id);
  }, [active, endsAtMs, offsetMs]);

  if (!active || remainingSeconds == null) return null;

  return (
    <Box className={styles.timer}>
      <Typography className={styles.timerText}>
        {remainingSeconds > 0 ? `Timer - ${formatTime(remainingSeconds)}` : 'Time is up'}
      </Typography>
    </Box>
  );
};

export default VotingTimerView;
